// src/components/exam/ExamSchedule.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Plus, X, Edit2, Trash2, Wand2, Calendar, Copy } from 'lucide-react';
import { useAuth } from '../../AuthContext';
import { toast } from 'react-toastify';
import { Tooltip } from '@mui/material';
import 'react-toastify/dist/ReactToastify.css';
import './ExamSchedule.css';
import configData from '../../configure';

const API_BASE_URL = configData.RESOURCES_API;
const SCHEDULES_API_URL = configData.SCHEDULES || `${configData.RESOURCES_API}/schedules`;
const EVENT_START_DATE = '2025-10-05T00:00:00Z';
const EVENT_END_DATE = '2030-12-31T23:59:59Z';
const DAYS_OF_WEEK = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const getTimeInMinutes = (timeValue) => {
  if (!timeValue || !String(timeValue).includes(':')) return null;
  const [hour, minute] = String(timeValue).split(':').map(Number);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
  return hour * 60 + minute;
};

const getNormalizedDuration = (durationValue) => {
  const parsed = parseInt(durationValue, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const hasScheduleConflict = (candidate, existing) => {
  const candidateDate = candidate.date || candidate.scheduledDate;
  const existingDate = existing.date || existing.scheduledDate;
  if (!candidateDate || !existingDate || candidateDate !== existingDate) return false;

  const candidateStart = getTimeInMinutes(candidate.startTime);
  const existingStart = getTimeInMinutes(existing.startTime);
  if (candidateStart === null || existingStart === null) return false;

  const candidateEnd = candidateStart + getNormalizedDuration(candidate.durationMins);
  const existingEnd = existingStart + getNormalizedDuration(existing.durationMins);

  return candidateStart < existingEnd && existingStart < candidateEnd;
};

const getCourseDisplayName = (courseList, courseId, fallbackTitle = 'Subject') => {
  const course = courseList.find((item) => String(item.courseId) === String(courseId));
  return course?.displayName || course?.courseName || fallbackTitle;
};

export default function ExamSchedule({ orgId }) {
  const { token, userRole, studentInfo, user } = useAuth();
  const effectiveOrgId = orgId || user?.orgId || studentInfo?.orgId || sessionStorage.getItem('orgId') || localStorage.getItem('orgId') || 'org1';
  const studentClassId = studentInfo?.classId || user?.classId || '';

  const [events, setEvents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [selectedClassId, setSelectedClassId] = useState('');
  const [schedules, setSchedules] = useState([]);
  const [courses, setCourses] = useState([]); // Used for both main view, edit, and add mode
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Generate Schedule modal state
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showClassDropdown, setShowClassDropdown] = useState(false);
  const [generateConfig, setGenerateConfig] = useState({
    startDate: '',
    interval: 1,
    startTime: '09:00',
    duration: '120',
    location: 'Main Hall',
    mode: 'consecutive' // 'consecutive' | 'skip-weekends'
  });
  // Multi-class selection for table view
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [activeTableClass, setActiveTableClass] = useState('');

  // courseCache per class for generate modal
  const courseCacheRef = useRef(new Map());

  const [scheduleRows, setScheduleRows] = useState([
    { courseId: '', date: '', startTime: '', durationMins: '45', location: '' }
  ]);

  const classCache = useRef(new Map());

  // Derive day of week from date for API
  const getDayOfWeekFromDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString + 'T00:00:00');
    const dayIndex = date.getUTCDay();
    return DAYS_OF_WEEK[(dayIndex + 6) % 7]; // Adjust for Monday = 0
  };

  // Readable day name for UI
  const getDisplayDayName = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return DAY_NAMES[date.getUTCDay()];
  };

  // Fetch Events
  useEffect(() => {
    if (!token) return;
    const fetchEvents = async () => {
      setLoadingEvents(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/events?orgId=${effectiveOrgId}&eventStartDate=${EVENT_START_DATE}&eventEndDate=${EVENT_END_DATE}&pageSize=100`,
          { headers: { accept: 'application/json', Authorization: token } }
        );
        if (!response.ok) throw new Error('Failed to fetch events');
        const data = await response.json();
        const examEvents = (data.items || [])
          .filter((event) => {
            const name = (event.eventName || '').toLowerCase();
            const type = event.eventType || '';
            return (
              type === 'EXAM_TERM' ||
              type === 'EXAM' ||
              name.includes('exam') ||
              name.includes('test') ||
              name.includes('assessment') ||
              name.includes('term')
            );
          })
          .map((event) => ({
            id: event.eventId,
            name: event.eventName?.trim() || 'Untitled Exam',
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setEvents(examEvents);
        if (examEvents.length > 0 && !selectedEvent) {
          setSelectedEvent(examEvents[0].id);
        }
      } catch (err) {
        console.error(err);
        toast.error('Failed to load exams');
      } finally {
        setLoadingEvents(false);
      }
    };
    fetchEvents();
  }, [token, effectiveOrgId]);

  // Fetch Classes
  useEffect(() => {
    if (!token) return;
    const fetchClasses = async () => {
      setLoadingClasses(true);
      try {
        const response = await fetch(`${API_BASE_URL}/classes/org/${effectiveOrgId}`, {
          headers: { accept: 'application/json', Authorization: token },
        });
        if (!response.ok) throw new Error('Failed to fetch classes');
        const data = await response.json();
        const classOptions = data
          .map((cls) => ({
            id: cls.classId,
            name: `${cls.className} ${cls.sectionName || ''}`.trim(),
          }))
          .sort((a, b) => {
            const getInfo = (label) => {
              const m = label.match(/Class (\d+)\s*(\w*)/i);
              return m ? { num: +m[1], sec: m[2].toUpperCase() || 'A' } : { num: 0, sec: label };
            };
            const A = getInfo(a.name);
            const B = getInfo(b.name);
            return A.num !== B.num ? A.num - B.num : A.sec.localeCompare(B.sec);
          });
        setClasses(classOptions);

        // Student logic: Force their class only
        if (userRole === 'STUDENT' && studentClassId) {
          const sClass = classOptions.find(c => String(c.id) === String(studentClassId));
          if (sClass) {
            setSelectedClassId(sClass.id);
            setSelectedClassIds([sClass.id]);
            setActiveTableClass(sClass.id);
          }
        } else if (classOptions.length > 0 && !selectedClassId) {
          setSelectedClassId(classOptions[0].id);
          setSelectedClassIds([classOptions[0].id]);
          setActiveTableClass(classOptions[0].id);
        }
      } catch (err) {

        console.error(err);
        toast.error('Failed to load classes');
      } finally {
        setLoadingClasses(false);
      }
    };
    fetchClasses();
  }, [token, effectiveOrgId]);

  // Fetch Courses for selected class (used in main view, edit, and add)
  useEffect(() => {
    if (!token || !selectedClassId) {
      setCourses([]);
      return;
    }
    const fetchCoursesForClass = async () => {
      if (classCache.current.has(selectedClassId)) {
        setCourses(classCache.current.get(selectedClassId));
        return;
      }
      setLoadingCourses(true);
      try {
        const res = await fetch(`${API_BASE_URL}/classes/${selectedClassId}`, {
          headers: { Authorization: token },
        });
        if (!res.ok) throw new Error('Failed to fetch class details');
        const classData = await res.json();
        const courseIds = classData.courseIds || [];
        if (courseIds.length === 0) {
          setCourses([]);
          classCache.current.set(selectedClassId, []);
          return;
        }
        const coursePromises = courseIds.map(id =>
          fetch(`${API_BASE_URL}/courses/${id}`, { headers: { Authorization: token } })
            .then(r => r.ok ? r.json() : { courseId: id, courseDetails: { courseName: 'Unknown', courseCode: 'N/A' } })
            .catch(() => ({ courseId: id, courseDetails: { courseName: 'Unknown', courseCode: 'N/A' } }))
        );
        const responses = await Promise.all(coursePromises);
        const courseList = responses.map((r, index) => {
          const details = r.courseDetails || {};
          const nameValue = details.courseName || 'Unknown Subject';
          const courseIdValue = r.courseId || courseIds[index];
          // More robust code extraction
          let codeValue = r.courseCode || details.courseCode;
          const code = codeValue && String(codeValue).trim() && String(codeValue).trim() !== 'N/A'
            ? String(codeValue).trim()
            : null;
          return {
            courseId: courseIdValue,
            courseName: nameValue,
            courseCode: code,
            displayName: code ? `${nameValue} (${code})` : nameValue,
          };
        });
        classCache.current.set(selectedClassId, courseList);
        setCourses(courseList);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load subjects');
        setCourses([]);
      } finally {
        setLoadingCourses(false);
      }
    };
    fetchCoursesForClass();
  }, [selectedClassId, token]);

  // Fetch Schedules
  const fetchSchedules = async () => {
    if (!selectedEvent || !token) return;
    setLoadingSchedules(true);
    setError(null);
    try {
      const response = await fetch(
        `${SCHEDULES_API_URL}?filterType=event&id=${selectedEvent}&orgId=${effectiveOrgId}&pageSize=100`,
        {
          headers: { accept: 'application/json', Authorization: token },
        }
      );
      if (!response.ok) {
        if (response.status === 404) {
          setSchedules([]);
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      const list = data?.items || data?.content || (Array.isArray(data) ? data : []);
      setSchedules(list);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to fetch schedule');
      toast.error('Failed to load schedule');
    } finally {
      setLoadingSchedules(false);
    }
  };

  useEffect(() => {
    fetchSchedules();
  }, [selectedEvent, token, effectiveOrgId]);

  // Helper: fetch courses for a class (with cache)
  const fetchCoursesForClassId = async (classId) => {
    if (classCache.current.has(classId)) return classCache.current.get(classId);
    const res = await fetch(`${API_BASE_URL}/classes/${classId}`, { headers: { Authorization: token } });
    if (!res.ok) return [];
    const classData = await res.json();
    const courseIds = classData.courseIds || [];
    if (courseIds.length === 0) { classCache.current.set(classId, []); return []; }
    const coursePromises = courseIds.map(id =>
      fetch(`${API_BASE_URL}/courses/${id}`, { headers: { Authorization: token } })
        .then(r => r.ok ? r.json() : { courseId: id, courseDetails: { courseName: 'Unknown' } })
        .catch(() => ({ courseId: id, courseDetails: { courseName: 'Unknown' } }))
    );
    const responses = await Promise.all(coursePromises);
    const courseList = responses.map((r, index) => {
      const details = r.courseDetails || {};
      const nameValue = details.courseName || 'Unknown Subject';
      const courseIdValue = r.courseId || courseIds[index];
      let codeValue = r.courseCode || details.courseCode;
      const code = codeValue && String(codeValue).trim() && String(codeValue).trim() !== 'N/A' ? String(codeValue).trim() : null;
      return { courseId: courseIdValue, courseName: nameValue, courseCode: code, displayName: code ? `${nameValue} (${code})` : nameValue };
    });
    classCache.current.set(classId, courseList);
    return courseList;
  };

  const getExistingItemsForClass = (classId) =>
    schedules
      .filter((schedule) => String(schedule.classId) === String(classId))
      .flatMap((schedule) =>
        Object.entries(schedule.scheduleItems || {}).flatMap(([day, dayItems]) =>
          dayItems.map((item, index) => ({
            ...item,
            _scheduleId: schedule.scheduleId,
            _day: day,
            _itemIndex: index,
          }))
        )
      );

  // Unified generate: creates one schedule per selected class using that class's own courses
  const handleGenerate = async () => {
    if (!generateConfig.startDate) { toast.error('Please set a start date'); return; }
    if (!selectedEvent) { toast.error('Please select an exam'); return; }
    if (selectedClassIds.length === 0) { toast.error('Please select at least one class'); return; }

    setGenerating(true);
    let successCount = 0;
    let failCount = 0;
    const workingDays = [1, 2, 3, 4, 5]; // Mon-Fri

    for (const classId of selectedClassIds) {
      try {
        const classCourses = await fetchCoursesForClassId(classId);
        if (classCourses.length === 0) { failCount++; continue; }

        const existingItems = getExistingItemsForClass(classId);
        const generatedItems = [];

        const scheduleItemsByDay = {};
        let currentDate = new Date(generateConfig.startDate);

        classCourses.forEach((course) => {
          // Always skip Sundays (day 0)
          while (currentDate.getDay() === 0) currentDate.setDate(currentDate.getDate() + 1);
          const dateStr = currentDate.toISOString().split('T')[0];
          const dayOfWeek = getDayOfWeekFromDate(dateStr);
          const [hour, minute] = generateConfig.startTime.split(':');
          const scheduledDateTime = `${dateStr}T${hour.padStart(2, '0')}:${minute}:00.000Z`;
          const end = new Date(scheduledDateTime);
          end.setMinutes(end.getMinutes() + parseInt(generateConfig.duration));
          const candidateItem = {
            courseId: course.courseId,
            scheduledDate: dateStr,
            startTime: generateConfig.startTime,
            durationMins: parseInt(generateConfig.duration, 10),
          };

          const conflictingExisting = existingItems.find((item) => hasScheduleConflict(candidateItem, item));
          if (conflictingExisting) {
            throw new Error(
              `Time conflict in ${classes.find((c) => c.id === classId)?.name || 'class'}: ` +
              `${getCourseDisplayName(classCourses, course.courseId)} overlaps with existing ` +
              `${getCourseDisplayName(classCourses, conflictingExisting.courseId, conflictingExisting.title)} on ${dateStr} at ${conflictingExisting.startTime}.`
            );
          }

          const conflictingGenerated = generatedItems.find((item) => hasScheduleConflict(candidateItem, item));
          if (conflictingGenerated) {
            throw new Error(
              `Time conflict in ${classes.find((c) => c.id === classId)?.name || 'class'}: ` +
              `${getCourseDisplayName(classCourses, course.courseId)} overlaps with ` +
              `${getCourseDisplayName(classCourses, conflictingGenerated.courseId)} on ${dateStr} at ${generateConfig.startTime}.`
            );
          }

          const item = {
            sequenceNumber: 1, itemType: 'LECTURE', title: course.displayName,
            courseId: course.courseId, facultyId: null, location: generateConfig.location,
            roomCapacity: 50, scheduledDate: dateStr, scheduledDateTime,
            startTime: generateConfig.startTime, durationMins: parseInt(generateConfig.duration),
            endTime: end.toTimeString().slice(0, 5), additionalInfo: { medium: 'English' },
          };
          generatedItems.push(candidateItem);
          if (!scheduleItemsByDay[dayOfWeek]) scheduleItemsByDay[dayOfWeek] = [];
          scheduleItemsByDay[dayOfWeek].push(item);
          currentDate.setDate(currentDate.getDate() + generateConfig.interval);
        });

        const cls = classes.find(c => c.id === classId);
        const eventName = events.find(e => e.id === selectedEvent)?.name || 'Exam';
        const payload = {
          academicYear: '2025-2026', classId, eventId: selectedEvent, orgId: effectiveOrgId,
          scheduleType: 'EXAM',
          scheduleName: `${eventName} - ${cls?.name || 'Class'} Schedule`,
          description: 'Exam schedule', effectiveFrom: '2025-06-01T00:00:00.000Z',
          effectiveTo: '2030-12-31T23:59:59.000Z',
          createdBy: user?.userId || user?.id || 'current-user',
          lastUpdatedBy: user?.userId || user?.id || 'current-user',
          scheduleItems: scheduleItemsByDay,
        };
        const res = await fetch(`${SCHEDULES_API_URL}`, {
          method: 'POST',
          headers: { 'accept': 'application/json', 'Authorization': token, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (res.ok) successCount++; else failCount++;
      } catch (err) { console.error(err); toast.error(err.message || 'Failed to generate schedule'); failCount++; }
    }

    setGenerating(false);
    setShowGenerateModal(false);
    if (successCount > 0) toast.success(`✅ Schedules created for ${successCount} class${successCount > 1 ? 'es' : ''}!`);
    if (failCount > 0) toast.error(`⚠️ Failed for ${failCount} class${failCount > 1 ? 'es' : ''}`);
    fetchSchedules();
  };

  // Modal Controls
  const openAddModal = () => {
    setIsEditMode(false);
    setEditingItem(null);
    setScheduleRows([{ courseId: '', date: '', startTime: '', durationMins: '45', location: '' }]);
    setShowModal(true);
  };

  const openEditModal = (schedule, day, itemIndex) => {
    const item = schedule.scheduleItems[day][itemIndex];
    setIsEditMode(true);
    setEditingItem({ schedule, day, itemIndex });
    setScheduleRows([{
      courseId: item.courseId || '',
      date: item.scheduledDate || '',
      startTime: item.startTime || '',
      durationMins: item.durationMins?.toString() || '45',
      location: item.location || '',
    }]);
    setShowModal(true);
  };

  // Row Management
  const addScheduleRow = () => {
    setScheduleRows([...scheduleRows, { courseId: '', date: '', startTime: '', durationMins: '45', location: '' }]);
  };

  const removeScheduleRow = (index) => {
    if (scheduleRows.length === 1) {
      toast.warning('At least one schedule item is required');
      return;
    }
    setScheduleRows(scheduleRows.filter((_, i) => i !== index));
  };

  const updateScheduleRow = (index, field, value) => {
    const updated = [...scheduleRows];
    updated[index][field] = value;
    setScheduleRows(updated);
  };

  // Delete Item
  const handleDelete = async (schedule, day, itemIndex) => {
    if (!window.confirm('Are you sure you want to delete this schedule item? This action cannot be undone.')) return;
    setDeleting(true);
    try {
      const updatedSchedule = JSON.parse(JSON.stringify(schedule));
      updatedSchedule.scheduleItems[day].splice(itemIndex, 1);
      if (updatedSchedule.scheduleItems[day].length === 0) {
        delete updatedSchedule.scheduleItems[day];
      }
      if (Object.keys(updatedSchedule.scheduleItems).length === 0) {
        await fetch(`${SCHEDULES_API_URL}/${schedule.scheduleId}`, {
          method: 'DELETE',
          headers: { 'accept': '*/*', Authorization: token },
        });
        toast.success('Schedule deleted completely.');
      } else {
        await fetch(`${SCHEDULES_API_URL}/${schedule.scheduleId}`, {
          method: 'PUT',
          headers: {
            'accept': 'application/json',
            'Authorization': token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updatedSchedule),
        });
        toast.success('Schedule item deleted successfully!');
      }
      fetchSchedules();
    } catch (err) {
      toast.error('Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  // Save (Add/Edit)
  const handleSave = async () => {
    if (!selectedEvent) {
      toast.error('Please select an exam.');
      return;
    }
    if (!selectedClassId) {
      toast.error('Please select a class.');
      return;
    }

    setSaving(true);

    try {
      const className = classes.find(c => c.id === selectedClassId)?.name || 'Selected Class';

      // 1. Validation: conflict inside current rows for the same class
      for (let i = 0; i < scheduleRows.length; i++) {
        const firstRow = scheduleRows[i];
        if (!firstRow.courseId || !firstRow.date || !firstRow.startTime) continue;

        for (let j = i + 1; j < scheduleRows.length; j++) {
          const secondRow = scheduleRows[j];
          if (!secondRow.courseId || !secondRow.date || !secondRow.startTime) continue;

          if (hasScheduleConflict(firstRow, secondRow)) {
            const firstSubject = getCourseDisplayName(courses, firstRow.courseId);
            const secondSubject = getCourseDisplayName(courses, secondRow.courseId);
            toast.error(
              `Time conflict in ${className}: ${firstSubject} and ${secondSubject} overlap on ${firstRow.date}.`
            );
            setSaving(false);
            return;
          }
        }
      }

      // 2. Validation: conflict against existing schedules already saved for the class
      const existingItems = getExistingItemsForClass(selectedClassId);

      for (const row of scheduleRows) {
        if (!row.courseId || !row.date || !row.startTime) continue;

        const conflictingItem = existingItems.find((item) => {
          if (isEditMode && editingItem) {
            const sameItem =
              String(item._scheduleId) === String(editingItem.schedule.scheduleId) &&
              item._day === editingItem.day &&
              item._itemIndex === editingItem.itemIndex;
            if (sameItem) return false;
          }
          return hasScheduleConflict(row, item);
        });

        if (conflictingItem) {
          const subject = getCourseDisplayName(courses, row.courseId);
          const conflictingSubject = getCourseDisplayName(courses, conflictingItem.courseId, conflictingItem.title);
          toast.error(
            `Time conflict in ${className}: ${subject} overlaps with existing ${conflictingSubject} on ${row.date} at ${conflictingItem.startTime}.`
          );
          setSaving(false);
          return;
        }
      }

      if (isEditMode) {
        // EDIT single item
        const { schedule, day: originalDay, itemIndex } = editingItem;
        const row = scheduleRows[0];
        if (!row.courseId || !row.date || !row.startTime) {
          toast.error('Please fill all required fields.');
          setSaving(false);
          return;
        }
        const selectedCourse = courses.find(c => c.courseId === row.courseId);
        const title = selectedCourse?.displayName || 'Unknown';
        const [hour, minute] = row.startTime.split(':');
        const scheduledDateTime = `${row.date}T${hour.padStart(2, '0')}:${minute}:00.000Z`;
        const newItem = {
          sequenceNumber: 1,
          itemType: "LECTURE",
          title,
          courseId: row.courseId,
          facultyId: null,
          location: row.location || "N/A",
          roomCapacity: 50,
          scheduledDate: row.date,
          scheduledDateTime,
          startTime: row.startTime,
          durationMins: parseInt(row.durationMins),
          endTime: (() => {
            const end = new Date(scheduledDateTime);
            end.setMinutes(end.getMinutes() + parseInt(row.durationMins));
            return end.toTimeString().slice(0, 5);
          })(),
          additionalInfo: { medium: "English" },
        };
        const newDay = getDayOfWeekFromDate(row.date);
        const updatedSchedule = JSON.parse(JSON.stringify(schedule));
        if (!updatedSchedule.scheduleItems[newDay]) {
          updatedSchedule.scheduleItems[newDay] = [];
        }
        if (originalDay !== newDay) {
          updatedSchedule.scheduleItems[originalDay].splice(itemIndex, 1);
          if (updatedSchedule.scheduleItems[originalDay].length === 0) {
            delete updatedSchedule.scheduleItems[originalDay];
          }
        }
        if (originalDay === newDay) {
          updatedSchedule.scheduleItems[newDay][itemIndex] = newItem;
        } else {
          updatedSchedule.scheduleItems[newDay].push(newItem);
        }
        const response = await fetch(`${SCHEDULES_API_URL}/${schedule.scheduleId}`, {
          method: 'PUT',
          headers: {
            'accept': 'application/json',
            'Authorization': token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updatedSchedule),
        });
        if (!response.ok) throw new Error('Update failed');
        toast.success('Item updated successfully!');
      } else {
        // ADD: Single class only
        const selectedClass = classes.find(c => c.id === selectedClassId);
        const className = selectedClass?.name || 'Unknown';

        const scheduleItemsByDay = {};
        for (const row of scheduleRows) {
          if (!row.courseId || !row.date || !row.startTime || !row.durationMins) {
            toast.error('Please fill all required fields in every row.');
            setSaving(false);
            return;
          }
          const dayOfWeek = getDayOfWeekFromDate(row.date);
          if (!dayOfWeek) {
            toast.error('Invalid date selected.');
            setSaving(false);
            return;
          }
          const selectedCourse = courses.find(c => c.courseId === row.courseId);
          if (!selectedCourse) {
            toast.error('Invalid subject selected.');
            setSaving(false);
            return;
          }
          const title = selectedCourse.displayName;
          const [hour, minute] = row.startTime.split(':');
          const scheduledDateTime = `${row.date}T${hour.padStart(2, '0')}:${minute}:00.000Z`;

          const item = {
            sequenceNumber: 1,
            itemType: "LECTURE",
            title,
            courseId: row.courseId,
            facultyId: null,
            location: row.location || "N/A",
            roomCapacity: 50,
            scheduledDate: row.date,
            scheduledDateTime,
            startTime: row.startTime,
            durationMins: parseInt(row.durationMins),
            endTime: (() => {
              const end = new Date(scheduledDateTime);
              end.setMinutes(end.getMinutes() + parseInt(row.durationMins));
              return end.toTimeString().slice(0, 5);
            })(),
            additionalInfo: { medium: "English" },
          };

          if (!scheduleItemsByDay[dayOfWeek]) scheduleItemsByDay[dayOfWeek] = [];
          scheduleItemsByDay[dayOfWeek].push(item);
        }

        const payload = {
          academicYear: "2025-2026",
          classId: selectedClassId,
          eventId: selectedEvent,
          orgId: effectiveOrgId,
          scheduleType: "EXAM",
          scheduleName: `${events.find(e => e.id === selectedEvent)?.name || 'Exam'} - ${className} Schedule`,
          description: "Exam schedule",
          effectiveFrom: "2025-06-01T00:00:00.000Z",
          effectiveTo: "2030-12-31T23:59:59.000Z",
          createdBy: user?.userId || user?.id || "current-user",
          lastUpdatedBy: user?.userId || user?.id || "current-user",
          scheduleItems: scheduleItemsByDay,
        };

        const response = await fetch(`${SCHEDULES_API_URL}`, {
          method: 'POST',
          headers: {
            'accept': 'application/json',
            'Authorization': token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          toast.success(`Schedule added for ${className}!`);
        } else {
          throw new Error('Failed to create schedule');
        }
      }

      setShowModal(false);
      setScheduleRows([{ courseId: '', date: '', startTime: '', durationMins: '45', location: '' }]);
      fetchSchedules();
    } catch (err) {
      console.error(err);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Display schedule table
  const getDisplayedSchedule = () => {
    const matching = schedules.filter(s => String(s.classId) === String(selectedClassId));
    const items = [];
    matching.forEach(schedule => {
      if (!schedule.scheduleItems) return;
      Object.entries(schedule.scheduleItems).forEach(([day, dayItems]) => {
        dayItems.forEach((item, itemIndex) => {
          const rawDate = item.scheduledDate || '';
          const formattedDate = rawDate.split('-').reverse().join('-');
          const timeParts = (item.startTime || '09:00').split(':');
          const hourStr = timeParts[0] || '09';
          const minute = timeParts[1] || '00';
          let hour = parseInt(hourStr, 10);
          const period = hour < 12 ? 'AM' : 'PM';
          if (hour === 0) hour = 12;
          if (hour > 12) hour -= 12;
          const formattedTime = `${hour.toString().padStart(2, '0')}:${minute} ${period}`;
          const hours = Math.floor(item.durationMins / 60);
          const mins = item.durationMins % 60;
          let duration = '';
          if (hours > 0) duration += `${hours}h `;
          if (mins > 0) duration += `${mins}m`;
          const courseIdStr = item.courseId ? String(item.courseId) : null;
          const course = courses.find(c => 
            (courseIdStr && String(c.courseId) === courseIdStr) || 
            (item.title && (c.courseName === item.title || c.displayName === item.title))
          );
          const subject = course ? (course.courseName || item.title) : (item.title || 'Untitled');
          const code = course ? course.courseCode : null;
          
          items.push({
            subject,
            courseCode: code || '—',
            date: formattedDate,
            time: formattedTime,
            duration: duration.trim() || `${item.durationMins}m`,
            room: item.location || 'N/A',
            _raw: { schedule, day, itemIndex },
          });
        });
      });
    });
    items.sort((a, b) => {
      const dateA = new Date(a.date.split(' ').reverse().join('-'));
      const dateB = new Date(b.date.split(' ').reverse().join('-'));
      return dateA - dateB || a.time.localeCompare(b.time);
    });
    return items;
  };

  const schedule = getDisplayedSchedule();
  const selectedEventName = events.find(e => e.id === selectedEvent)?.name || 'Schedule';
  const selectedClassName = classes.find(c => c.id === selectedClassId)?.name || 'Unknown Class';
  const pageTitle = `${selectedEventName} - ${selectedClassName}`;

  if (!token) return <div className="es-container">Loading authentication...</div>;

  return (
    <>

      <div className="es-container">
        {/* Header — 2 buttons only */}
        <div className="es-header">
          <h2 className="es-title">Schedule Management</h2>
          <div className="es-actions">
            {userRole === 'ADMIN' && (
              <>
                <Tooltip title="Add a single exam slot" arrow>
                  <button
                    className="es-btn es-btn-green"
                    onClick={openAddModal}
                    disabled={!selectedEvent || !selectedClassId}
                    aria-label="Add single item"
                  >
                    <Plus size={18} />
                  </button>
                </Tooltip>
                <Tooltip title="Auto-generate schedules for one or more classes" arrow>
                  <button
                    className="es-btn"
                    onClick={() => setShowGenerateModal(true)}
                    disabled={!selectedEvent}
                    style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', boxShadow: '0 2px 8px rgba(99,102,241,0.35)' }}
                  >
                    <Wand2 size={18} /> Generate Schedule
                  </button>
                </Tooltip>
              </>
            )}
          </div>
        </div>

        {/* Filters — Exam dropdown + multi-class pills */}
        <div className="es-search-box">
          <div className="es-filters" style={{ flexWrap: 'wrap', gap: '12px', alignItems: 'flex-start' }}>
            {/* Exam selector */}
            {loadingEvents ? (
              <div className="es-select">Loading exams...</div>
            ) : (
              <select value={selectedEvent} onChange={(e) => setSelectedEvent(e.target.value)} className="es-select">
                <option value="">Select Exam</option>
                {events.map(event => (
                  <option key={event.id} value={event.id}>{event.name}</option>
                ))}
              </select>
            )}

            {/* Single class dropdown (for viewing schedule) */}
            {loadingClasses ? (
              <div className="es-select">Loading classes...</div>
            ) : userRole !== 'STUDENT' ? (
              <select
                value={selectedClassId}
                onChange={(e) => { setSelectedClassId(e.target.value); setActiveTableClass(e.target.value); }}
                className="es-select"
              >
                <option value="">Select Class</option>
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>{cls.name}</option>
                ))}
              </select>
            ) : (
              <div className="es-select" style={{ opacity: 0.7 }}>{classes.find(c => c.id === selectedClassId)?.name || 'Your Class'}</div>
            )}
          </div>
        </div>

        {/* Generate Schedule Modal */}
        {showGenerateModal && (
          <div className="es-modal-overlay" onClick={() => !generating && setShowGenerateModal(false)}>
            <div className="es-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '560px' }}>
              <div className="es-modal-header">
                <h2 className="es-modal-title">Generate Exam Schedule</h2>
                <button className="es-modal-close" onClick={() => setShowGenerateModal(false)} disabled={generating}><X size={24} /></button>
              </div>
              <div className="es-modal-body">
                {/* Custom checkbox dropdown for class multi-select */}
                <div style={{ marginBottom: '24px', position: 'relative' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                    Select Classes
                  </label>
                  {/* Trigger button */}
                  <div
                    onClick={() => setShowClassDropdown(v => !v)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', backgroundColor: '#fff', userSelect: 'none' }}
                  >
                    <span style={{ fontSize: '14px', color: selectedClassIds.length === 0 ? '#9ca3af' : '#1e293b', fontWeight: selectedClassIds.length > 0 ? '600' : '400' }}>
                      {selectedClassIds.length === 0 ? 'Choose classes...' : `${selectedClassIds.length} class${selectedClassIds.length > 1 ? 'es' : ''} selected`}
                    </span>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ transform: showClassDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                      <path d="M4 6l4 4 4-4" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  {/* Dropdown panel */}
                  {showClassDropdown && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, backgroundColor: '#fff', border: '1.5px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', marginTop: '4px', maxHeight: '220px', overflowY: 'auto' }}>
                      {/* Select All row */}
                      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', backgroundColor: '#f8fafc' }}>
                        <input
                          type="checkbox"
                          checked={selectedClassIds.length === classes.length && classes.length > 0}
                          onChange={(e) => setSelectedClassIds(e.target.checked ? classes.map(c => c.id) : [])}
                          style={{ width: '15px', height: '15px', cursor: 'pointer', accentColor: '#4f46e5' }}
                        />
                        <span style={{ fontSize: '13px', fontWeight: '700', color: '#374151' }}>Select All</span>
                      </label>
                      {/* Individual classes */}
                      {classes.map(cls => {
                        const checked = selectedClassIds.includes(cls.id);
                        return (
                          <label key={cls.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', cursor: 'pointer', backgroundColor: checked ? '#eef2ff' : '#fff', borderBottom: '1px solid #f8fafc' }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => setSelectedClassIds(checked ? selectedClassIds.filter(id => id !== cls.id) : [...selectedClassIds, cls.id])}
                              style={{ width: '15px', height: '15px', cursor: 'pointer', accentColor: '#4f46e5' }}
                            />
                            <span style={{ fontSize: '14px', color: checked ? '#4f46e5' : '#374151', fontWeight: checked ? '600' : '400' }}>{cls.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Config grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>Start Date *</label>
                    <input type="date" value={generateConfig.startDate} onChange={(e) => setGenerateConfig({ ...generateConfig, startDate: e.target.value })} className="es-modal-input" />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>Start Time</label>
                    <input type="time" value={generateConfig.startTime} onChange={(e) => setGenerateConfig({ ...generateConfig, startTime: e.target.value })} className="es-modal-input" />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>Duration (mins)</label>
                    <input type="number" min="30" step="15" value={generateConfig.duration} onChange={(e) => setGenerateConfig({ ...generateConfig, duration: e.target.value })} className="es-modal-input" />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>Days Between Exams</label>
                    <input type="number" min="1" value={generateConfig.interval} onChange={(e) => setGenerateConfig({ ...generateConfig, interval: parseInt(e.target.value) || 1 })} className="es-modal-input" />
                  </div>
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>Location</label>
                    <input type="text" value={generateConfig.location} onChange={(e) => setGenerateConfig({ ...generateConfig, location: e.target.value })} placeholder="Hall / Room" className="es-modal-input" />
                  </div>
                </div>


              </div>
              <div className="es-modal-footer">
                <button className="es-modal-btn es-modal-btn-primary" onClick={handleGenerate} disabled={generating || selectedClassIds.length === 0 || !generateConfig.startDate}>
                  {generating ? `Generating (${selectedClassIds.length} classes)...` : `Generate for ${selectedClassIds.length} Class${selectedClassIds.length !== 1 ? 'es' : ''}`}
                </button>
                <button className="es-modal-btn es-modal-btn-secondary" onClick={() => setShowGenerateModal(false)} disabled={generating}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="es-table-section">
          {loadingSchedules ? (
            <div className="es-no-data"><p>Loading schedule...</p></div>
          ) : error ? (
            <div className="es-no-data"><p>Error: {error}</p></div>
          ) : schedule.length > 0 ? (
            <>
              <h3 className="es-table-title">{pageTitle}</h3>
              <div className="es-table-wrapper">
                <table className="es-table">
                  <thead>
                    <tr>
                      <th>Course Code</th>
                      <th>Course Name</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Duration</th>
                      <th>Room</th>
                      {userRole === 'ADMIN' && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.map((item, i) => (
                      <tr key={i}>
                        <td><span className="es-code-badge">{item.courseCode}</span></td>
                        <td>{item.subject}</td>
                        <td>{item.date}</td>
                        <td>{item.time}</td>
                        <td>{item.duration}</td>
                        <td>{item.room}</td>
                        {userRole === 'ADMIN' && (
                          <td className="es-actions-cell">
                            <Tooltip title="Edit Schedule Details" arrow>
                              <button className="es-action-btn es-action-edit" onClick={() => openEditModal(item._raw.schedule, item._raw.day, item._raw.itemIndex)} disabled={deleting}>
                                <Edit2 size={18} />
                              </button>
                            </Tooltip>
                            <Tooltip title="Remove Scheduled Item" arrow>
                              <button className="es-action-btn es-action-delete" onClick={() => handleDelete(item._raw.schedule, item._raw.day, item._raw.itemIndex)} disabled={deleting}>
                                <Trash2 size={18} />
                              </button>
                            </Tooltip>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="es-no-data">
              <p>{selectedEvent && selectedClassId ? 'No schedule items found. Use Quick Actions to create one!' : 'Please select an exam and class.'}</p>
            </div>
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="es-modal-overlay" onClick={() => setShowModal(false)}>
            <div className="es-modal" onClick={(e) => e.stopPropagation()}>
              <div className="es-modal-header">
                <h2 className="es-modal-title">
                  {isEditMode ? 'Edit' : 'Add'} Schedule Item{scheduleRows.length > 1 ? 's' : ''}
                </h2>
                <button className="es-modal-close" onClick={() => setShowModal(false)}>
                  <X size={24} />
                </button>
              </div>
              <div className="es-modal-body">
                {/* Class Selection - Add Mode Only */}
                {!isEditMode && (
                  <div style={{ marginBottom: '28px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                      Select Class
                    </label>
                    <select
                      value={selectedClassId}
                      onChange={(e) => setSelectedClassId(e.target.value)}
                      className="es-modal-input"
                      style={{ width: '100%', padding: '12px', fontSize: '15px' }}
                      required
                    >
                      <option value="">Choose a class</option>
                      {classes.map(cls => (
                        <option key={cls.id} value={cls.id}>{cls.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Edit Mode - Show Class Name */}
                {isEditMode && (
                  <input
                    type="text"
                    value={classes.find(c => c.id === editingItem?.schedule?.classId)?.name || 'Unknown Class'}
                    className="es-modal-input"
                    disabled
                    style={{ marginBottom: '28px' }}
                  />
                )}

                {/* Schedule Rows */}
                <div>
                  <h4 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: '600' }}>
                    Schedule Items ({scheduleRows.length})
                  </h4>
                  {(() => {
                    const databaseScheduledIds = new Set();
                    schedules
                      .filter(s => s.classId === selectedClassId)
                      .forEach(s => {
                        Object.values(s.scheduleItems || {}).flat().forEach(item => {
                          databaseScheduledIds.add(item.courseId);
                        });
                      });

                    return scheduleRows.map((row, index) => {
                      const availableCourses = courses.filter(course => {
                        const isAlreadyInDB = databaseScheduledIds.has(course.courseId);
                        if (isEditMode && editingItem) {
                          const { schedule, day, itemIndex } = editingItem;
                          const originalItem = schedule.scheduleItems[day]?.[itemIndex];
                          if (course.courseId === originalItem?.courseId) return true;
                        }
                        return !isAlreadyInDB;
                      });

                      return (
                        <div
                          key={index}
                          style={{
                            border: '1px solid #e5e7eb',
                            borderRadius: '10px',
                            padding: '18px',
                            marginBottom: '20px',
                            backgroundColor: '#f9fafb',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#1d4ed8' }}>
                              Subject {index + 1}
                              {row.date && ` — ${getDisplayDayName(row.date)}`}
                            </h5>
                            {scheduleRows.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeScheduleRow(index)}
                                style={{
                                  background: '#fee2e2',
                                  color: '#dc2626',
                                  border: 'none',
                                  borderRadius: '6px',
                                  width: '32px',
                                  height: '32px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                            {loadingCourses ? (
                              <div className="es-modal-input">Loading subjects...</div>
                            ) : courses.length === 0 ? (
                              <div className="es-modal-input" style={{ color: '#dc2626' }}>No subjects in this class</div>
                            ) : (
                              <select
                                value={row.courseId}
                                onChange={(e) => updateScheduleRow(index, 'courseId', e.target.value)}
                                className="es-modal-input"
                                required
                              >
                                <option value="">Select Subject</option>
                                {availableCourses.map(course => (
                                  <option key={course.courseId} value={course.courseId}>
                                    {course.displayName}
                                  </option>
                                ))}
                              </select>
                            )}
                            <input
                              type="date"
                              value={row.date}
                              onChange={(e) => updateScheduleRow(index, 'date', e.target.value)}
                              className="es-modal-input"
                              required
                            />
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' }}>
                            <input
                              type="time"
                              value={row.startTime}
                              onChange={(e) => updateScheduleRow(index, 'startTime', e.target.value)}
                              className="es-modal-input"
                              required
                            />
                            <input
                              type="number"
                              value={row.durationMins}
                              onChange={(e) => updateScheduleRow(index, 'durationMins', e.target.value)}
                              className="es-modal-input"
                              min="1"
                              placeholder="Duration (mins)"
                              required
                            />
                            <input
                              type="text"
                              value={row.location}
                              onChange={(e) => updateScheduleRow(index, 'location', e.target.value)}
                              placeholder="Room No"
                              className="es-modal-input"
                            />
                          </div>
                        </div>
                      );
                    });
                  })()
                  }

                  {
                    !isEditMode && (
                      <button
                        type="button"
                        onClick={addScheduleRow}
                        style={{
                          width: '100%',
                          padding: '14px',
                          backgroundColor: '#eff6ff',
                          border: '2px dashed #3b82f6',
                          borderRadius: '10px',
                          color: '#2563eb',
                          fontWeight: '600',
                          fontSize: '15px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '10px',
                          marginBottom: '10px'
                        }}
                      >
                        <Plus size={20} /> Add Another Subject
                      </button>
                    )
                  }
                </div>
              </div>

              <div className="es-modal-footer">
                <button
                  className="es-modal-btn es-modal-btn-primary"
                  onClick={handleSave}
                  disabled={saving || loadingCourses || !selectedClassId}
                >
                  {saving ? 'Saving...' : isEditMode ? 'Update Item' : 'Add Schedule Items'}
                </button>
                <button className="es-modal-btn es-modal-btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
